import { useCallback, useRef, useState } from 'react';
import { usePushEhrCredentialsV2, useOnboardEhr } from 'src/hooks/ehr';
import { getApiErrorMessage } from 'src/services';
import { showToastSuccess } from 'src/utilities/toast';
import { showToastError } from 'src/utilities/errortoast';

// Service account used to kick off EHR onboarding on behalf of the practice.
// Same one OP uses; it's pre-provisioned in every Frontegg tenant.
const ONBOARDING_USER_EMAIL = 'api@insighthealth.ai';
// Last-resort fallback if a registry entry forgets to include the timezone
// field. Each EHR should ask for it explicitly via the form.
const DEFAULT_ONBOARDING_TIMEZONE = 'UTC';

const toMessage = (error, fallback) =>
  getApiErrorMessage(error) || error?.message || fallback;

// Orchestrates the push-then-onboard flow declared by a registry entry:
//   1. If the entry has a `credentials` block, push the credentials. Some
//      EHRs (e.g. OP) write the same credentials under multiple
//      ehr_system keys -- `entry.credentials.ehrSystems` overrides that.
//   2. Onboard the org via /ehr/onboarding using the canonical ehr_system.
//
// `onboarding` values are spread into the request as-is, so any extra
// per-EHR fields declared in the registry (e.g. `timezone`, future
// `department_id`) flow through without code changes here.
//
// `isSubmitting` is owned by this hook (not derived from the individual
// mutation `isPending` flags) so it stays true across the entire
// push->onboard sequence. Deriving from `isSavingCreds || isOnboarding`
// would briefly flip false between the two awaits, re-enabling the
// button. A ref additionally guards against re-entry from a fast
// double-click before React commits the state update.
export const useEhrOnboarding = (entry) => {
  const { pushCredentials } = usePushEhrCredentialsV2();
  const { onboardEhr } = useOnboardEhr();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const inFlightRef = useRef(false);

  const submit = useCallback(
    async ({ orgUuid, credentials, onboarding }) => {
      if (inFlightRef.current) {
        return { success: false, step: 'reentry' };
      }
      if (!entry || !orgUuid) {
        showToastError('Missing organization or EHR configuration');
        return { success: false };
      }

      inFlightRef.current = true;
      setIsSubmitting(true);
      try {
        if (entry.credentials) {
          const ehrSystems = entry.credentials.ehrSystems || [entry.ehrSystem];
          try {
            await Promise.all(
              ehrSystems.map((ehrSystem) =>
                pushCredentials({
                  org_uuid: orgUuid,
                  ehr_system: ehrSystem,
                  credentials,
                })
              )
            );
            showToastSuccess(`${entry.label} credentials saved successfully`);
          } catch (error) {
            showToastError(
              toMessage(error, `Failed to save ${entry.label} credentials`)
            );
            return { success: false, step: 'credentials', error };
          }
        }

        try {
          const onboardingValues = onboarding || {};
          await onboardEhr({
            org_uuid: orgUuid,
            ehr_system: entry.ehrSystem,
            user_email: ONBOARDING_USER_EMAIL,
            timezone: DEFAULT_ONBOARDING_TIMEZONE,
            ...onboardingValues,
          });
          showToastSuccess(`Organization onboarded to ${entry.label}`);
          return { success: true };
        } catch (error) {
          showToastError(
            toMessage(
              error,
              entry.credentials
                ? `Credentials saved, but ${entry.label} onboarding failed. Please retry.`
                : `Failed to onboard organization to ${entry.label}`
            )
          );
          return { success: false, step: 'onboarding', error };
        }
      } finally {
        inFlightRef.current = false;
        setIsSubmitting(false);
      }
    },
    [entry, pushCredentials, onboardEhr]
  );

  return {
    submit,
    isSubmitting,
  };
};
